import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { Divider, Grid } from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import SearchIcon from '@material-ui/icons/Search';
import { Element, scroller } from 'react-scroll';
import routes from '../../../../constants/routes';
import { profileRepertoireResponseToUxRepresentation } from '../../adapters';
import RepertoireCard from '../../../../components/EditComponents/RepertoireCard';
import { selectChosenProfileData, selectChosenProfileRepertoire, selectWishlistRemainingInfo } from '../../selectors';
import { getRouteWithSlug } from '../../../../utils/common';
import Button from '../../../../components/Button';
import { performancesAggregationTypes, repertoireStatuses, TP } from '../../../../constants/consts';
import NextLink from '../../../../components/NextLink';
import H3 from '../../../../components/H3';
import FormikAutocompleteSelect from '../../../../components/Fields/FormikAutocompleteSelect';
import useAggregationFilter from '../../../../utils/hooks/useAggregationFilter';
import { PRODUCTIONS_AGGREGATIONS, PROFILE_REPERTOIRE } from '../../../../constants/endpointConstants';
import useResetOptions from '../../../../utils/hooks/useResetOptions';
import { selectAggregationsOptions, selectBufferValueData } from '../../../App/selectors';
import usePerformancesTabStyles from './PerformancesTab/styles';
import { deleteEntity, getWholeList, updateOne, useBufferValueAction } from '../../../App/actions';
import {
  getChosenProfileRepertoireAction,
  getWishlistRepertoireAction,
  removeRepertoire,
  updateRepertoireStatusAction,
} from '../../actions';
import { getFlatFormValues } from '../../../../utils/formUtils';
import { fields } from '../../../../components/EditComponents/RepertoireCard/RoleSwitch';
import { Router } from '../../../../src/i18n';
import ActionStatusModal from '../../../../components/ActionStatusModal';
import TextButton from '../../../../components/TextButton';
import FbCircularProgress from '../../../../components/FbCircularProgress';
import { createDate } from '../../../../utils/date';

const getRepertoireByProfileId = (dispatch, sagaRoutine, profileId, queryParams) => {
  if (profileId) {
    dispatch(
      getWholeList({
        endpoint: PROFILE_REPERTOIRE(profileId),
        sagaRoutine,
        params: { queryParams: { ...queryParams, show_hidden: true } },
      }),
    );
  }
};

export const getProfileRepertoire = (dispatch, profileId, queryParams) => {
  getRepertoireByProfileId(dispatch, getChosenProfileRepertoireAction, profileId, queryParams);
};

export const getWishlist = (dispatch, profileId) => {
  const queryParams = { query: repertoireStatuses.WISHLIST };
  getRepertoireByProfileId(dispatch, getWishlistRepertoireAction, profileId, queryParams);
};

export const composerKeyGenerator = composerId => `composer-${composerId}`;

export const RepertoireLayout = ({ t, tab }) => {
  const styles = usePerformancesTabStyles();
  const dispatch = useDispatch();
  const [deleteRepertoire, setRepertoireForDeletion] = React.useState(null);

  const repertoire = useSelector(selectChosenProfileRepertoire());
  const activeProfile = useSelector(selectChosenProfileData());
  const remainingInfo = useSelector(selectWishlistRemainingInfo());
  const deletingRepertoire = useSelector(selectBufferValueData());

  const profileId = get(activeProfile, 'id');
  const { added, total } = remainingInfo;
  const canAddMore = total > added;

  const repertoireAdapted = useMemo(() => profileRepertoireResponseToUxRepresentation(repertoire), [repertoire]);

  const staticOptions = [
    { value: '', label: t(`${TP}.m_ALL`) },
    { value: repertoireStatuses.WISHLIST, label: t(`${TP}.WISHLIST`) },
    { value: `${repertoireStatuses.RETIRED},${repertoireStatuses.PAST}`, label: t(`${TP}.m_LISTSCHEDULE`) },
  ];

  const { profileAgg, workAgg } = performancesAggregationTypes;
  const works = useSelector(selectAggregationsOptions(workAgg));

  const searchFilter = useAggregationFilter(PRODUCTIONS_AGGREGATIONS);
  const resetOptions = useResetOptions();

  const queryChange = query => {
    getProfileRepertoire(dispatch, profileId, query);
  };

  const handleStatusChange = values => {
    const flatValues = getFlatFormValues(values);
    const status = get(flatValues, `${fields.status.name}`);
    const repertoireId = get(values, 'repertoireId');
    dispatch(
      updateOne({
        endpoint: PROFILE_REPERTOIRE(profileId),
        sagaRoutine: updateRepertoireStatusAction,
        id: repertoireId,
        params: {
          status,
        },
      }),
    );
  };

  const onRoleDelete = () => {
    dispatch(
      deleteEntity({
        endpoint: PROFILE_REPERTOIRE(profileId),
        sagaRoutine: useBufferValueAction,
        id: deleteRepertoire?.repertoireId,
        callback: itemId => {
          dispatch(removeRepertoire(itemId));
          setRepertoireForDeletion(null);
        },
        successMessage: `${get(deleteRepertoire, 'workRole.name')} is deleted`,
      }),
    );
  };

  useEffect(() => {
    const { anchor } = Router.query;
    if (anchor) {
      scroller.scrollTo(anchor, {
        duration: 800,
        offset: -150,
        delay: 0,
        smooth: 'easeInOutQuart',
      });
    }
  }, [repertoire]);

  return (
    <>
      <div className={styles.heading}>
        <H3>{t(get(tab, 'name'))}</H3>
        <div>
          <div className={styles.lastUpdate}>{t(`${TP}.m_LASTUPDATE`)}:</div>
          <time className={styles.date} dateTime={repertoireAdapted.lastUpdated.date}>
            {createDate(repertoireAdapted.lastUpdated.date).format('YYYY-MM-DD HH:MM:SS')}
          </time>
        </div>
      </div>
      <section>
        {activeProfile && !canAddMore && (
          <span className={styles.warning}>{t(`${TP}.WISHLIST_LIMIT_REACHED`, { limit: total })}</span>
        )}
        <div className={styles.actionBlock}>
          <Formik
            onSubmit={values => {
              const query = getFlatFormValues(values);
              queryChange(query);
              resetOptions(workAgg);
            }}
            initialValues={{ [workAgg]: null, status: staticOptions[0] }}
          >
            {({ submitForm }) => (
              <Form className={styles.filterForm}>
                <Grid container spacing={1} justify="flex-start">
                  <Grid item sm={3} xs={12}>
                    <Field
                      instanceId="status"
                      key="status"
                      name="status"
                      onChange={submitForm}
                      options={staticOptions}
                      label={t(`${TP}.m_SELECT2`)}
                      component={FormikAutocompleteSelect}
                    />
                  </Grid>
                  <Grid item sm={9} xs={12}>
                    <Field
                      instanceId={workAgg}
                      label={t(`${TP}.m_MUSICALWORK`)}
                      name={workAgg}
                      placeholder={t(`${TP}.FN_SEARCH_BY_MUSICAL_WORK`)}
                      onChange={submitForm}
                      onInputTextChange={value => {
                        searchFilter(workAgg, value, { [profileAgg]: profileId });
                      }}
                      options={works}
                      isLoading={works?.loading}
                      minSearchCharsLength={0}
                      component={FormikAutocompleteSelect}
                      isClearable
                      isEditable
                      customComponents={{
                        DropdownIndicator: () => <SearchIcon />,
                      }}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
          {activeProfile && (
            <Button disabled={!canAddMore} className={styles.addButton}>
              <NextLink
                href={`${routes.ARTISTS}/[id]/edit/add-repertoire`}
                as={`${getRouteWithSlug(routes.ARTISTS, activeProfile)}/edit/add-repertoire`}
                className={styles.addButtonLink}
              >
                {t(`${TP}.ADD_FUTURE_REPERTOIRE`)}{' '}
              </NextLink>
            </Button>
          )}
        </div>
      </section>
      <Divider classes={{ root: styles.divider }} />
      <section>
        {repertoire?.loading ? (
          <FbCircularProgress />
        ) : (
          repertoireAdapted?.composers.map(card => {
            const elementName = composerKeyGenerator(get(card, 'composer.id'));
            return (
              <Element name={elementName} key={elementName}>
                <RepertoireCard
                  repertoireEntry={card}
                  onStatusChange={handleStatusChange}
                  onDelete={setRepertoireForDeletion}
                />
              </Element>
            );
          })
        )}
      </section>
      {deleteRepertoire && (
        <ActionStatusModal
          isOpened
          onClose={() => setRepertoireForDeletion(null)}
          type="warning"
          title={t(`${TP}.FN_DELETE_REPERTOIRE_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={() => setRepertoireForDeletion(null)} key="cancel-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={onRoleDelete} key="cdelte-button" isLoading={deletingRepertoire?.loading}>
              {t(`${TP}.m_DELETE`)}
            </Button>,
          ]}
        />
      )}
    </>
  );
};

RepertoireLayout.propTypes = {
  t: PropTypes.func.isRequired,
  tab: PropTypes.object.isRequired,
  remainingInfo: PropTypes.shape({
    added: PropTypes.number,
    total: PropTypes.number,
  }),
};

export default RepertoireLayout;
